import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, MenuItem, Select } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Field, FormikErrors, FormikValues, useFormikContext } from "formik";
import React, { ChangeEvent, useCallback, useMemo } from "react";
import countryList from "react-select-country-list";
import { YELLOW } from "../../../../themes/components/utils";
import { CustomTooltip } from "../../components";
import { CustomInput } from "../../elements";
import { useResponsiveness } from "../../hooks/utils/useResponsiveness";
import { selectMenuProps } from "../../landing-page/subscribe/job-requests/search-form";

type ProfessionalInformationFormProps = {
  prefix?: string;
  disabled?: boolean;
  onChange?: (e: ChangeEvent) => void;
  onBlur?: (e: FocusEvent) => void;
  values?: FormikValues;
  errors?: FormikErrors<FormikValues>;
};

const useStyles = makeStyles({
  select: {
    color: "#FFFFFF",
    fontSize: 16,
    height: 68,
    border: "2px solid white",
    "&:hover": {
      borderColor: YELLOW,
    },
    "&.MuiOutlinedInput-root": {
      backgroundColor: "transparent",
    },

    "& .MuiSelect-icon": {
      color: "#FFFFFF",
    },

    "& .MuiPaper-root": {
      height: "400px !important",
    },
  },
});

export const AccountInformationForm: React.FC<
  ProfessionalInformationFormProps
> = ({ prefix = "", disabled = false, onBlur, onChange, values }) => {
  const styles = useStyles();
  const options = useMemo(() => countryList().getData(), []);
  const { setFieldValue } = useFormikContext();
  const { isSmall, isMedium } = useResponsiveness();

  const getValue = useCallback(
    (name: string) => {
      if (prefix) {
        return values?.[prefix][name] ?? "";
      }

      return values?.[name] ?? "";
    },
    [prefix, values]
  );

  return (
    <Box
      style={
        disabled
          ? {
              pointerEvents: "none",
              opacity: 0.5,
            }
          : {}
      }
      display="flex"
      flexDirection="column"
      alignItems={isSmall ? "center" : "flex-end"}
      rowGap={12}
    >
      <Box
        display="flex"
        columnGap={4}
        flexDirection={isSmall ? "column" : "row"}
        rowGap={10}
      >
        <Box width={isMedium ? 280 : 400}>
          <Field
            component={CustomInput}
            fullWidth
            onChange={onChange}
            onBlur={onBlur}
            value={getValue("firstName")}
            variant="outlined"
            placeholder="First Name"
            name={`${prefix}${prefix ? "." : ""}firstName`}
            id={`${prefix}${prefix ? "." : ""}firstName`}
          />
        </Box>
        <Box width={isMedium ? 280 : 400}>
          <Field
            component={CustomInput}
            fullWidth
            onChange={onChange}
            onBlur={onBlur}
            value={getValue("lastName")}
            variant="outlined"
            placeholder={"Last Name"}
            name={`${prefix}${prefix ? "." : ""}lastName`}
            id={`${prefix}${prefix ? "." : ""}lastName`}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        columnGap={4}
        rowGap={10}
        flexDirection={isSmall ? "column" : "row"}
      >
        <Box width={isMedium ? 280 : 400}>
          <Field
            component={CustomInput}
            fullWidth
            onChange={onChange}
            onBlur={onBlur}
            value={getValue("addressLine1")}
            variant="outlined"
            placeholder="Address Line 1"
            name={`${prefix}${prefix ? "." : ""}addressLine1`}
            id={`${prefix}${prefix ? "." : ""}addressLine1`}
          />
        </Box>
        <Box width={isMedium ? 280 : 400}>
          <Field
            component={CustomInput}
            fullWidth
            onChange={onChange}
            onBlur={onBlur}
            value={getValue("addressLine2")}
            variant="outlined"
            placeholder={"Address Line 2"}
            name={`${prefix}${prefix ? "." : ""}addressLine2`}
            id={`${prefix}${prefix ? "." : ""}addressLine2`}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        columnGap={4}
        flexDirection={isSmall ? "column" : "row"}
        rowGap={10}
      >
        <Box width={isMedium ? 280 : 400}>
          <Field
            component={CustomInput}
            fullWidth
            onChange={onChange}
            onBlur={onBlur}
            value={getValue("city")}
            variant="outlined"
            placeholder="City"
            name={`${prefix}${prefix ? "." : ""}city`}
            id={`${prefix}${prefix ? "." : ""}city`}
          />
        </Box>
        <Box width={isMedium ? 280 : 400}>
          <Field
            required
            component={CustomInput}
            fullWidth
            onChange={onChange}
            onBlur={onBlur}
            value={getValue("state")}
            variant="outlined"
            placeholder={"State/Province/Region"}
            name={`${prefix}${prefix ? "." : ""}state`}
            id={`${prefix}${prefix ? "." : ""}state`}
          />
        </Box>
      </Box>

      <Box
        display="flex"
        columnGap={4}
        flexDirection={isSmall ? "column" : "row"}
        rowGap={10}
      >
        <Box width={isMedium ? 280 : 400}>
          <Field
            component={CustomInput}
            fullWidth
            onChange={onChange}
            onBlur={onBlur}
            value={getValue("zipCode")}
            variant="outlined"
            placeholder="Zip/Postal Code"
            name={`${prefix}${prefix ? "." : ""}zipCode`}
            id={`${prefix}${prefix ? "." : ""}zipCode`}
          />
        </Box>
        <Box width={isMedium ? 280 : 400}>
          <Select
            disableUnderline
            placeholder="Country"
            sx={
              "country" === (getValue("country") || "country")
                ? {
                    "& .MuiSelect-select": {
                      color: "rgba(255,255,255,0.6)",
                      paddingLeft: "30px",
                    },
                  }
                : {}
            }
            MenuProps={selectMenuProps}
            className={styles.select}
            onChange={(e) => {
              setFieldValue(
                `${prefix}${prefix ? "." : ""}country`,
                e.target.value
              );
            }}
            fullWidth
            name={`${prefix}${prefix ? "." : ""}country`}
            value={getValue("country") || "country"}
            IconComponent={KeyboardArrowDownIcon}
          >
            <MenuItem value="country">Country</MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.label}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      <Box
        display="flex"
        columnGap={4}
        flexDirection={isSmall ? "column" : "row"}
        rowGap={10}
      >
        <Box width={isMedium ? 280 : 400}>
          <Field
            component={CustomInput}
            fullWidth
            onChange={onChange}
            onBlur={onBlur}
            value={getValue("phoneNumber")}
            variant="outlined"
            placeholder={"Phone Number"}
            name={`${prefix}${prefix ? "." : ""}phoneNumber`}
            id={`${prefix}${prefix ? "." : ""}phoneNumber`}
          />
        </Box>
        <CustomTooltip title={"Email cannot be changed"} placement="top">
          <div>
            <Box
              width={isMedium ? 280 : 400}
              sx={{ pointerEvents: "none", cursor: "forbidden" }}
            >
              <Field
                component={CustomInput}
                fullWidth
                onChange={onChange}
                onBlur={onBlur}
                value={getValue("email")}
                variant="outlined"
                placeholder={"Email"}
                name={`${prefix}${prefix ? "." : ""}email`}
                id={`${prefix}${prefix ? "." : ""}email`}
              />
            </Box>
          </div>
        </CustomTooltip>
      </Box>
    </Box>
  );
};
